import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import type { GridLoader, TableViewRange } from "@fortex/api";
import { Button, DataGrid, Input, englishGridLabels, hungarianGridLabels } from "@fortex/map";
import { useApp } from "../app/context";
import { cellText, hasHistory, rowKeysOf, tableGridColumns, tableIdColumn, tablePageSizes } from "./tableView";
import type { TableRow } from "./tableView";

function sameColumns(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((name, index) => name === right[index]);
}

export function TablePage() {
  const { api, labels, lang } = useApp();
  const tableName = useParams().tableName ?? "";
  const [columns, setColumns] = useState<string[]>([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [refreshes, setRefreshes] = useState(0);
  const range = useRef<TableViewRange>({ fromDate: "", toDate: "" });
  const keys = useRef(new WeakMap<TableRow, string>());

  useEffect(() => {
    range.current = { fromDate, toDate };
  }, [fromDate, toDate]);

  useEffect(() => {
    document.title = labels.tableTitle;
  }, [labels]);

  const load = useCallback<GridLoader<TableRow>>(
    async (query, signal) => {
      const page = await api.tableView(tableName, query, range.current, signal);
      const rowKeys = rowKeysOf(page.columns, page.rows);

      page.rows.forEach((row, index) => keys.current.set(row, rowKeys[index]));
      setColumns(current => (sameColumns(current, page.columns) ? current : page.columns));

      return { rows: page.rows, total: page.totalCount };
    },
    [api, tableName]
  );

  const rowKey = useCallback((row: TableRow) => keys.current.get(row) ?? JSON.stringify(row), []);

  const gridColumns = useMemo(
    () =>
      tableGridColumns(columns, (row, column) => {
        const text = cellText(row[column], lang);

        return <span title={text}>{text}</span>;
      }),
    [columns, lang]
  );

  const toolbar = hasHistory(columns) ? (
    <fieldset className="fxapp-table-history" data-fxmap="table-history">
      <legend>{labels.tableFilterOptions}</legend>
      <label className="fxapp-table-date">
        <span>{labels.tableChangedFrom}</span>
        <Input type="date" value={fromDate} aria-label={labels.tableChangedFrom} onChange={event => setFromDate(event.target.value)} data-fxmap="table-from" />
      </label>
      <label className="fxapp-table-date">
        <span>{labels.tableChangedTo}</span>
        <Input type="date" value={toDate} aria-label={labels.tableChangedTo} onChange={event => setToDate(event.target.value)} data-fxmap="table-to" />
      </label>
      <Button kind="primary" size="sm" label={labels.tableRefresh} onClick={() => setRefreshes(current => current + 1)} data-fxmap="table-refresh" />
    </fieldset>
  ) : null;

  return (
    <div className="fxapp-table-page" data-fxmap="table-page">
      <DataGrid<TableRow>
        key={refreshes}
        columns={gridColumns}
        rowKey={rowKey}
        load={load}
        labels={lang === "en" ? englishGridLabels : hungarianGridLabels}
        defaultSortField={tableIdColumn}
        pageSizes={tablePageSizes}
        pageNumbers={5}
        clearableSort
        resizableColumns
        defaultFiltersOpen
        toolbar={toolbar}
        dense
        dataFxmap="table-grid"
      />
    </div>
  );
}
